<script setup lang="ts">
import {
  computed,
  ref,
  onMounted,
  onBeforeUnmount,
  onActivated,
  onDeactivated,
} from 'vue';
import { mesIconMap } from '@/lib/mes-icon-map';
const props = withDefaults(
  defineProps<{
    name?: string;
    legacy?: any;
    filled?: boolean;
    size?: number | string;
    label?: string;
    motion?: 'loading' | 'success';
    active?: boolean;
  }>(),
  { filled: false, active: true },
);
const visible = ref(true);
const visibility = () =>
  (visible.value = document.visibilityState === 'visible');
let subscribed = false;
onMounted(() => {
  if (props.motion) {
    subscribed = true;
    visibility();
    document.addEventListener('visibilitychange', visibility);
  }
});
onBeforeUnmount(() => {
  if (subscribed) document.removeEventListener('visibilitychange', visibility);
});
onDeactivated(() => (visible.value = false));
onActivated(() => visibility());
const assets = import.meta.glob('@/assets/mes-icons/v1/*.svg', {
  eager: true,
  query: '?raw',
  import: 'default',
});
const normalized = computed(() =>
  String(props.name || props.legacy?.name || props.legacy?.__name || '')
    .replace(/Icon$/, '')
    .replace(/[^a-zA-Z0-9]/g, '')
    .toLowerCase(),
);
const id = computed(() =>
  props.motion === 'loading'
    ? 'D114'
    : props.motion === 'success'
      ? 'D40'
      : mesIconMap[normalized.value],
);
const svg = computed(() => {
  const filename =
    id.value +
    '-' +
    (props.filled || props.motion === 'success' ? 'filled' : 'outline') +
    '.svg';
  const source = Object.entries(assets).find(([path]) =>
    path.endsWith('/' + filename),
  )?.[1] as string | undefined;
  return props.motion === 'success'
    ? source?.replace(/<path /g, '<path pathLength="100" ')
    : source;
});
</script>
<template>
  <span
    v-if="svg"
    class="mes-icon inline-flex shrink-0"
    :class="{
      'mes-loading': motion === 'loading' && active && visible,
      'mes-success': motion === 'success' && active && visible,
    }"
    :style="
      size
        ? {
            width: typeof size === 'number' ? size + 'px' : size,
            height: typeof size === 'number' ? size + 'px' : size,
          }
        : undefined
    "
    :role="label ? 'img' : undefined"
    :aria-label="label"
    :aria-hidden="label ? undefined : true"
    v-html="svg"
  /><component
    v-else-if="legacy"
    :is="legacy"
    :size="size"
    :aria-label="label"
    :aria-hidden="label ? undefined : true"
  />
</template>
<style scoped>
:global(:where(.mes-icon)) {
  width: 1em;
  height: 1em;
  vertical-align: middle;
}
.mes-icon :deep(svg) {
  width: 100%;
  height: 100%;
}
.mes-loading :deep(path[pathLength]) {
  animation: mes-orbit 1.8s linear infinite;
}
.mes-success :deep(g[stroke='white'] path) {
  stroke-dasharray: 100;
  stroke-dashoffset: 100;
  animation: mes-confirm 0.44s ease-out forwards;
}
@keyframes mes-orbit {
  0% {
    stroke-dashoffset: -8;
    stroke-dasharray: 28 72;
  }
  50% {
    stroke-dashoffset: -58;
    stroke-dasharray: 44 56;
  }
  100% {
    stroke-dashoffset: -108;
    stroke-dasharray: 28 72;
  }
}
@keyframes mes-confirm {
  to {
    stroke-dashoffset: 0;
  }
}
@media (prefers-reduced-motion: reduce) {
  .mes-icon :deep(*) {
    animation: none !important;
    stroke-dashoffset: 0;
  }
}
:global(html[data-document-hidden='true']) .mes-icon :deep(*) {
  animation-play-state: paused !important;
}
</style>
